import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SwiperOptions } from 'swiper';
import { CategoryListService } from './category-deals-list.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { CategoryProducts } from './model/category-products.model';
import { ScreenSizeService } from '../services/screen-size.service';
import { OverlayService } from 'src/app/shared/overlay.service';
import { Renderer2 } from '@angular/core';
import {
    AppliedFilterIds,
    Filter,
    SortLabel,
} from '../filter-data/filter-data.model';
import {
    CurrencyLanguageService,
    LocaleData,
} from '../shared/currency-language.service';
import { DeviceCommsService } from '../services/device-comms.service';
import {
    addFilterIdsToQueryStringAndSearch,
    addSortingDataToQueryString,
} from './category-sort-bar/category-sort-bar.utils';
import {
    addClassToSearchDatesComponent,
    removeClassFromSearchDatesComponent,
} from '../utilities/utils';
import { RouteParamsService } from '../services/route-params.service';
import { screenSizes } from '../utilities/theme';
import { RewardsService } from '../services/rewards.service';
import { BannerImageService } from '../services/banner-image.service';
import { NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
import {
    BannerTrackingService,
    BannerPlacement,
} from '../services/banner.service';
import { ProductCardEntry } from '@app/controls/product-card/product-card.model';
import { FilterApplicationService } from '@app/filter-data/filter-application.service';
import { gtmDealClick } from '../utilities/ga.utils';
import { CategoryAllDealsComponent } from './category-all-deals/category-all-deals.component';

declare let $: any;

@Component({
    selector: 'app-category-list',
    templateUrl: './category-deals-list.component.html',
    styleUrls: ['./category-deals-list.component.scss'],
})
export class CategoryListComponent implements OnInit, OnDestroy {
    categoryData: ProductCardEntry[] = [];
    loadingMoreDeals = false;

    @ViewChild('allDeals') allDeals: CategoryAllDealsComponent;

    private boundRedirectToLink: (url: string) => void;
    private subscription: Subscription = Subscription.EMPTY;
    private paramsSubscription: Subscription = Subscription.EMPTY;
    private categoryDealsSubscription: Subscription = Subscription.EMPTY;
    private moreCategoryDealsSubscription: Subscription = Subscription.EMPTY;
    private currencyLanguageSubscription: Subscription = Subscription.EMPTY;
    private rewardPointsSubscription: Subscription = Subscription.EMPTY;
    private filtersSubscription: Subscription = Subscription.EMPTY;
    private bannerSubscription: Subscription = Subscription.EMPTY;
    private isMobileSubscription: Subscription = Subscription.EMPTY;
    private overlayVisibilitySubscription: Subscription = Subscription.EMPTY;
    private eventsSubscription: Subscription = Subscription.EMPTY;

    filterIdsFromQueryString2: AppliedFilterIds[] = [];
    appliedFilterIds2: AppliedFilterIds[] = [];

    banners: Record<string, BannerPlacement> = {};
    isMobile: boolean = false;

    bannerMappings = [
        { index: 7, sectionId: 3, key: 'stays', alt: 'Stays Banner' },
        {
            index: 7,
            sectionId: 8,
            key: 'dayPackage',
            alt: 'Day Package Banner',
        },
        {
            index: 7,
            sectionId: 6,
            key: 'beautySpa',
            alt: 'Beauty & Spa Banner',
        },
        {
            index: 7,
            sectionId: 4,
            key: 'restaurants',
            alt: 'Restaurants Banner',
        },
        {
            index: 7,
            sectionId: 7,
            key: 'thingsToDo',
            alt: 'Things To Do Banner',
        },
        { index: 7, sectionId: 9, key: 'rodrigues', alt: 'Rodrigues Banner' },
        { index: 2, sectionId: 19, key: 'events', alt: 'Events Banner' },
    ];

    filterSortLabel: SortLabel;
    bannerConfig: SwiperOptions = {
        pagination: {
            el: '.swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        slidesPerView: 1,
        autoplay: true,
    };

    totalRecords: number = 0;
    busyLoadingData: boolean = true;

    scrollablePane: Element;
    currencySymbol: 'Rs.' | 'EUR';
    locale: 'en-MU' | 'fr-RE';
    rewardPointsMoney: number = 0;
    localeData: LocaleData;
    urlFilterParams: string;
    filterIds: AppliedFilterIds[] = [];

    public sectionId: number;
    public sectionName: string;
    private currentPage = 1;

    constructor(
        public categoryService: CategoryListService,
        public screenSizeService: ScreenSizeService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private currencyLanguageService: CurrencyLanguageService,
        private deviceCommsService: DeviceCommsService,
        private routeParamsService: RouteParamsService,
        private rewardService: RewardsService,
        private overlayService: OverlayService,
        private renderer: Renderer2,
        public bannerService: BannerImageService,
        private bannerTrackingService: BannerTrackingService,
        private filterApplicationService: FilterApplicationService
    ) {
        this.boundRedirectToLink = this.bannerService.redirectToLink.bind(
            this.bannerService
        );
    }

    get areFiltersApplied(): boolean {
        return (
            this.appliedFilterIds2?.length > 0 ||
            this.filterIds?.length > 0 ||
            this.filterIdsFromQueryString2?.length > 0
        );
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.paramsSubscription?.unsubscribe();
        this.categoryDealsSubscription?.unsubscribe();
        this.moreCategoryDealsSubscription?.unsubscribe();
        this.currencyLanguageSubscription?.unsubscribe();
        this.rewardPointsSubscription?.unsubscribe();
        this.filtersSubscription?.unsubscribe();
        this.bannerSubscription?.unsubscribe();
        this.isMobileSubscription?.unsubscribe();
        this.overlayVisibilitySubscription?.unsubscribe();
        this.eventsSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.getCategoryData();
        document.body.scrollTop = 0;
        this.scrollablePane = document.getElementsByClassName('pane')[0];
        this.currencyLanguageService.getCurrency().subscribe((currency) => {
            this.currencySymbol = currency;
        });
        this.currencyLanguageSubscription = this.currencyLanguageService
            .getLocaleForCurrency()
            .subscribe((locale) => {
                this.locale = locale;
            });
        this.rewardPointsSubscription =
            this.rewardService.rewardsPoints$.subscribe((rewards) => {
                if (rewards) {
                    this.rewardPointsMoney = rewards.rewardPointsMoney;
                }
            });
        this.filtersSubscription =
            this.filterApplicationService.$filterSubject.subscribe((filters) =>
                this.applyFilters(filters)
            );
        addClassToSearchDatesComponent();

        this.eventsSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.appliedFilterIds2 = [];
            }
        });

        this.urlFilterParams = this.activatedRoute.queryParams['filters'];

        this.bannerSubscription = this.bannerService
            .getBanners('plp')
            .subscribe((banners) => {
                this.banners = banners;
            });

        this.isMobileSubscription =
            this.screenSizeService.isMobileWidth.subscribe((mobile) => {
                this.isMobile = mobile;
            });

        this.overlayVisibilitySubscription =
            this.overlayService.overlayVisible$.subscribe(
                (isVisible: boolean) => {
                    const mdCategoryListingsRow = document.querySelector(
                        '.md-category-listings'
                    );
                    if (mdCategoryListingsRow) {
                        const childrenArray = Array.from(
                            mdCategoryListingsRow.children
                        );
                        childrenArray.forEach((child: Element) => {
                            this.renderer.setStyle(
                                child,
                                'pointer-events',
                                isVisible ? 'none' : 'auto'
                            );
                            this.renderer.setStyle(
                                child,
                                'filter',
                                isVisible ? 'blur(1.6px)' : 'none'
                            );
                        });
                    }

                    const appFilterData = document.querySelector('.row');
                    if (appFilterData) {
                        this.renderer.setStyle(
                            appFilterData,
                            'pointer-events',
                            isVisible ? 'none' : 'auto'
                        );
                        this.renderer.setStyle(
                            appFilterData,
                            'filter',
                            isVisible ? 'blur(1.6px)' : 'none'
                        );
                    }
                }
            );
    }

    handleBannerClick(bannerKey: string, idx: number): void {
        this.bannerTrackingService.handleBannerClick(
            bannerKey,
            idx,
            this.banners,
            this.boundRedirectToLink,
            'plp'
        );
    }

    onBannerSlide(event: NgbSlideEvent, bannerKey: string): void {
        this.bannerTrackingService.onBannerSlide(
            event,
            bannerKey,
            this.banners
        );
    }

    private getFilterIdsFromParamsAndFilterComponent(): AppliedFilterIds[] {
        return this.appliedFilterIds2.length > 0
            ? this.appliedFilterIds2
            : this.filterIdsFromQueryString2;
    }

    private getBannerUrl(
        screenWidth: number,
        config: { mobile: string; desktop: string }
    ): string {
        return screenWidth < screenSizes.mobile
            ? config.mobile
            : config.desktop;
    }

    private parseFilterParams(filterParams: string): AppliedFilterIds[] {
        return filterParams
            ? filterParams.split(',').map((filter) => {
                  const [filterTypeId, filterId] = filter.split('-');
                  return {
                      filterTypeId: +filterTypeId,
                      filterId: +filterId,
                  };
              })
            : [];
    }

    getCategoryData() {
        this.paramsSubscription = this.activatedRoute.params.subscribe(
            (params) => {
                this.sectionId = Number(params['sectionId']);
                this.sectionName = params['sectionName'];
                this.routeParamsService.setSectionId(this.sectionId);
            }
        );
        this.subscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.queryParams,
            this.currencyLanguageService.getLocaleData(),
        ]).subscribe(([_, queryParams, locale]) => {
            this.busyLoadingData = true;

            const filterParams: string = queryParams['filters'];
            this.urlFilterParams = filterParams;
            this.filterIdsFromQueryString2 =
                this.parseFilterParams(filterParams);

            const sortParam: string = queryParams['sort'];
            if (sortParam) {
                this.filterSortLabel = sortParam as SortLabel;
            }

            const filterIdsToApply =
                this.getFilterIdsFromParamsAndFilterComponent();
            this.localeData = locale;

            this.categoryDealsSubscription = this.categoryService
                .getCategoryDetails(
                    filterIdsToApply,
                    this.filterSortLabel,
                    locale.language,
                    this.sectionId,
                    locale.country
                )
                .subscribe({
                    next: (response: CategoryProducts) => {
                        this.busyLoadingData = false;
                        this.totalRecords = response.total;
                        this.categoryData = [...response.data];
                        this.filterApplicationService.changeNrOfResults(
                            this.totalRecords
                        );

                        if (this.categoryService.scrollPosition) {
                            setTimeout(() => {
                                this.scrollablePane.scrollTop =
                                    this.categoryService.scrollPosition;
                            }, 0);
                        }
                    },
                });
        });
    }

    onScroll() {
        if (this.busyLoadingData) return;

        if (this.categoryData.length < this.totalRecords) {
            this.loadingMoreDeals = true;
            this.currentPage += 1;

            this.filterIds = this.parseFilterParams(this.urlFilterParams);

            this.moreCategoryDealsSubscription = this.categoryService
                .getMoreCategoryDetails(
                    this.currentPage,
                    this.filterIds,
                    this.filterSortLabel,
                    this.localeData.language,
                    this.sectionId,
                    this.localeData.country
                )
                .subscribe({
                    next: (response: CategoryProducts) => {
                        this.categoryData = response.data;
                        this.loadingMoreDeals = false;
                    },
                });
        } else if (
            this.allDeals?.categoryData?.length < this.allDeals?.totalRecords
        ) {
            this.allDeals.onScroll();
        }
    }

    applyFilters(filters: Filter[]) {
        this.appliedFilterIds2 =
            filters?.flatMap((f) =>
                f.filterValues.map((fv) => ({
                    filterTypeId: f.filterTypeId,
                    filterId: fv.filterId,
                }))
            ) || [];

        addFilterIdsToQueryStringAndSearch(this.router, this.appliedFilterIds2);
        this.currentPage = 1;
    }

    applySorting(sortLabel: SortLabel) {
        this.filterSortLabel = sortLabel;
        addSortingDataToQueryString(this.router, sortLabel);
        this.currentPage = 1;
    }

    onImageError(event: any) {
        event.target.src = 'assets/img/defaultIcon.png';
    }

    redirectToProdDetail(id: number, productPath: string, name: string) {
        gtmDealClick(id, name, this.sectionName);
        this.categoryService.scrollPosition = this.scrollablePane.scrollTop;
        this.deviceCommsService.openLinkInNewTabIfBrowser(
            `/deal/${productPath}/${id}`
        );
    }

    goToPromotionLink(link: string) {
        this.deviceCommsService.openLinkInNewTabOrBrowser(link);
    }

    onParentDivClick(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    onImageClick(event: MouseEvent) {
        event.stopPropagation();
    }
}
